import { useQuery } from '@tanstack/react-query';
import { keccak256, decodeAbiParameters, type Hex } from 'viem';
import { CCTP_ATTESTATION_API, CCTP_ATTESTATION_POLL_INTERVAL } from '../config/cctp';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export type CctpAttestationStatus = 'pending' | 'complete';

export interface CctpAttestationData {
  status: CctpAttestationStatus;
  /** Hex-encoded message bytes from the MessageSent event (used in receiveMessage) */
  message?: Hex;
  /** Hex-encoded attestation signature from Circle (used in receiveMessage) */
  attestation?: Hex;
}

/**
 * keccak256("MessageSent(bytes)")
 * This is the event emitted by MessageTransmitterV2 when a cross-chain message is sent.
 * The single topic (after the event signature) encodes the ABI-encoded message bytes.
 */
const MESSAGE_SENT_TOPIC =
  '0x8c5261668696ce22758910d05bab8f186d6eb247ceac2af2e82c7dc17669b036' as const;

/**
 * Parse the `MessageSent(bytes message)` event from a transaction receipt to
 * extract the raw message bytes emitted by MessageTransmitterV2.
 *
 * The event is a non-indexed `bytes` param, so it is ABI-encoded in log.data.
 */
function parseMessageFromReceipt(
  logs: readonly { topics: readonly Hex[]; data: Hex }[],
): Hex {
  const log = logs.find(
    (l) => l.topics[0]?.toLowerCase() === MESSAGE_SENT_TOPIC.toLowerCase(),
  );
  if (!log) {
    throw new Error(
      'MessageSent event not found in transaction receipt. ' +
        'The depositForBurn tx may not have been mined on the correct contract.',
    );
  }

  // The event has one dynamic parameter (bytes), ABI-encoded in log.data
  const [messageBytes] = decodeAbiParameters(
    [{ name: 'message', type: 'bytes' }],
    log.data,
  );

  return messageBytes as Hex;
}

/**
 * Polls the Circle Attestation API until the attestation for a CCTP burn is available.
 *
 * Flow:
 * 1. Fetch the transaction receipt to extract the MessageSent event
 * 2. Compute messageHash = keccak256(message bytes)
 * 3. Poll Circle API at `CCTP_ATTESTATION_API/{messageHash}`
 * 4. Return { status, message, attestation } when complete
 *
 * Only enabled when `txHash` and `sourceChainId` are provided.
 *
 * @param txHash       - Transaction hash of the depositForBurn call
 * @param sourceChainId - EVM chain ID where the burn tx was sent
 */
export function useCctpAttestation(
  txHash: Hex | undefined,
  sourceChainId: number | undefined,
) {
  return useQuery<CctpAttestationData>({
    queryKey: ['cctpAttestation', txHash, sourceChainId],
    queryFn: async (): Promise<CctpAttestationData> => {
      if (!txHash || !sourceChainId) {
        throw new Error('txHash and sourceChainId are required');
      }

      // Step 1: get tx receipt and parse the message bytes
      const publicClient = getSourceChainPublicClient(sourceChainId);
      const receipt = await publicClient.getTransactionReceipt({ hash: txHash });

      const messageBytes = parseMessageFromReceipt(
        receipt.logs as readonly { topics: readonly Hex[]; data: Hex }[],
      );

      // Step 2: compute messageHash for the attestation API
      const messageHash = keccak256(messageBytes);

      // Step 3: poll Circle attestation API
      const url = `${CCTP_ATTESTATION_API}/${messageHash}`;
      const response = await fetch(url);

      if (!response.ok) {
        // 404 / 5xx — attestation not ready yet, treated as pending
        return { status: 'pending', message: messageBytes };
      }

      const json = (await response.json()) as {
        status: string;
        attestation?: string;
      };

      if (json.status === 'complete' && json.attestation) {
        return {
          status: 'complete',
          message: messageBytes,
          attestation: json.attestation as Hex,
        };
      }

      return { status: 'pending', message: messageBytes };
    },
    enabled: !!txHash && !!sourceChainId,
    // Keep polling until attestation is complete
    refetchInterval: (query) => {
      if (query.state.data?.status === 'complete') return false;
      return CCTP_ATTESTATION_POLL_INTERVAL;
    },
    // Don't cache stale attestation data — always check freshness
    staleTime: 0,
    // Retain the last result between refetches so UI doesn't flicker
    placeholderData: (prev) => prev,
  });
}
